/** Cómo se ve y cómo se edita el valor de cada tipo de propiedad. */
import { useEffect, useRef, useState } from 'react'

import { useEspacio } from '../contexto'
import { Flotante } from '../Menu'
import { fechaCorta, opcionDe } from './logica'
import type { FechaValor, Opcion, Propiedad } from './tipos'

export function Pastilla({ opcion }: { opcion: Opcion }) {
  return <span className={`pastilla pastilla--${opcion.color}`}>{opcion.name}</span>
}

interface Props {
  prop: Propiedad
  valor: unknown
  editable: boolean
  alCambiar: (valor: unknown) => void
  /** Crea una opción nueva en el esquema y devuelve su id (o null si falla). */
  alCrearOpcion?: (propId: string, nombre: string) => string | null
}

export function Celda({ prop, valor, editable, alCambiar, alCrearOpcion }: Props) {
  switch (prop.type) {
    case 'checkbox':
      return (
        <label className="celda celda--casilla">
          <input
            type="checkbox"
            checked={valor === true}
            disabled={!editable}
            onChange={(e) => alCambiar(e.target.checked)}
            aria-label={prop.name}
          />
        </label>
      )
    case 'select':
    case 'status':
    case 'multi_select':
      return <CeldaDeOpciones {...{ prop, valor, editable, alCambiar, alCrearOpcion }} />
    case 'person':
      return <CeldaDePersonas {...{ prop, valor, editable, alCambiar }} />
    case 'date':
      return <CeldaDeFecha {...{ prop, valor, editable, alCambiar }} />
    default:
      return <CeldaDeTexto {...{ prop, valor, editable, alCambiar }} />
  }
}

/** Una persona del portal: un círculo con su inicial y su nombre. */
export function Persona({ nombre, solo = false }: { nombre: string; solo?: boolean }) {
  return (
    <span className="persona" title={nombre}>
      <span className="persona__inicial" aria-hidden="true">{nombre.trim().charAt(0).toUpperCase()}</span>
      {!solo && <span className="persona__nombre">{nombre}</span>}
    </span>
  )
}

function CeldaDePersonas({ prop, valor, editable, alCambiar }: Omit<Props, 'alCrearOpcion'>) {
  const { personas } = useEspacio()
  const [abierta, setAbierta] = useState(false)
  const ancla = useRef<HTMLButtonElement>(null)
  const elegidas = Array.isArray(valor) ? (valor as string[]) : []
  const nombres = elegidas.map((id) => personas.find((p) => p.id === id)?.nombre)

  return (
    <>
      <button ref={ancla} className="celda celda--opciones" aria-label={prop.name} aria-haspopup="listbox"
              disabled={!editable && elegidas.length === 0}
              onClick={() => editable && setAbierta((v) => !v)}>
        {elegidas.length === 0 ? (
          <span className="celda__vacio">{editable ? 'Vacío' : ''}</span>
        ) : (
          elegidas.map((id, i) => <Persona key={id} nombre={nombres[i] ?? 'Alguien que ya no está'} />)
        )}
      </button>
      {abierta && (
        <SelectorDePersonas ancla={ancla} elegidas={elegidas}
          alCambiar={(ids) => alCambiar(ids.length ? ids : null)} alCerrar={() => setAbierta(false)} />
      )}
    </>
  )
}

function SelectorDePersonas({ ancla, elegidas, alCambiar, alCerrar }: {
  ancla: React.RefObject<HTMLElement | null>; elegidas: string[]
  alCambiar: (ids: string[]) => void; alCerrar: () => void
}) {
  const { personas } = useEspacio()
  const [q, setQ] = useState('')
  const lista = personas.filter((p) => p.nombre.toLowerCase().includes(q.trim().toLowerCase()))
  return (
    <Flotante ancla={ancla} alCerrar={alCerrar} ancho={250}>
      <div className="selector">
        <input autoFocus className="selector__buscar" placeholder="Buscar a alguien…" value={q}
               onChange={(e) => setQ(e.target.value)} aria-label="Buscar persona" />
        <div className="selector__lista" role="listbox">
          {lista.map((p) => (
            <button key={p.id} role="option" aria-selected={elegidas.includes(p.id)}
                    className="selector__opcion"
                    onClick={() => alCambiar(elegidas.includes(p.id) ? elegidas.filter((x) => x !== p.id) : [...elegidas, p.id])}>
              <Persona nombre={p.nombre} />
              {elegidas.includes(p.id) && <span className="selector__marca">✓</span>}
            </button>
          ))}
          {lista.length === 0 && <div className="selector__vacio">Nadie coincide</div>}
        </div>
        {elegidas.length > 0 && <button className="selector__quitar" onClick={() => alCambiar([])}>Quitar a todos</button>}
      </div>
    </Flotante>
  )
}

function CeldaDeTexto({ prop, valor, editable, alCambiar }: Omit<Props, 'alCrearOpcion'>) {
  const actual = valor == null ? '' : String(valor)
  const [texto, setTexto] = useState(actual)
  useEffect(() => setTexto(actual), [actual])

  const confirmar = () => {
    if (texto === actual) return
    if (prop.type === 'number') {
      if (texto.trim() === '') return alCambiar(null)
      const n = Number(texto.replace(',', '.'))
      if (Number.isFinite(n)) alCambiar(n)
      else setTexto(actual)
    } else {
      alCambiar(texto.trim() === '' ? null : texto)
    }
  }

  const enlace = prop.type === 'url' && /^https?:\/\//i.test(actual)
  return (
    <div className="celda">
      <input
        className="celda__entrada"
        type="text"
        inputMode={prop.type === 'number' ? 'decimal' : undefined}
        value={texto}
        readOnly={!editable}
        placeholder={editable ? 'Vacío' : ''}
        onChange={(e) => setTexto(e.target.value)}
        onBlur={confirmar}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
          if (e.key === 'Escape') {
            setTexto(actual)
            ;(e.target as HTMLInputElement).blur()
          }
        }}
        aria-label={prop.name}
      />
      {enlace && (
        <a className="celda__enlace" href={actual} target="_blank" rel="noopener noreferrer"
           aria-label="Abrir enlace" title="Abrir enlace">
          ↗
        </a>
      )}
    </div>
  )
}

function CeldaDeFecha({ prop, valor, editable, alCambiar }: Omit<Props, 'alCrearOpcion'>) {
  const inicio = (valor as FechaValor | undefined)?.start?.slice(0, 10) ?? ''
  return (
    <div className="celda celda--fecha">
      {editable ? (
        <input
          className="celda__entrada"
          type="date"
          value={inicio}
          onChange={(e) => alCambiar(e.target.value || null)}
          aria-label={prop.name}
        />
      ) : (
        <span className="celda__texto">{fechaCorta(inicio)}</span>
      )}
    </div>
  )
}

function CeldaDeOpciones({ prop, valor, editable, alCambiar, alCrearOpcion }: Props) {
  const [abierta, setAbierta] = useState(false)
  const ancla = useRef<HTMLButtonElement>(null)
  const multiple = prop.type === 'multi_select'
  const elegidas: string[] = multiple
    ? Array.isArray(valor) ? (valor as string[]) : []
    : typeof valor === 'string' ? [valor] : []
  const visibles = elegidas.map((id) => opcionDe(prop, id)).filter((o): o is Opcion => !!o)

  return (
    <>
      <button
        ref={ancla}
        className="celda celda--opciones"
        onClick={() => editable && setAbierta((v) => !v)}
        disabled={!editable && visibles.length === 0}
        aria-label={prop.name}
        aria-haspopup="listbox"
      >
        {visibles.length === 0 ? (
          <span className="celda__vacio">{editable ? 'Vacío' : ''}</span>
        ) : (
          visibles.map((o) => <Pastilla key={o.id} opcion={o} />)
        )}
      </button>
      {abierta && (
        <SelectorDeOpciones
          ancla={ancla}
          prop={prop}
          elegidas={elegidas}
          multiple={multiple}
          alCrearOpcion={alCrearOpcion}
          alElegir={(ids) => {
            alCambiar(multiple ? (ids.length ? ids : null) : (ids[0] ?? null))
            if (!multiple) setAbierta(false)
          }}
          alCerrar={() => setAbierta(false)}
        />
      )}
    </>
  )
}

function SelectorDeOpciones({
  ancla, prop, elegidas, multiple, alElegir, alCerrar, alCrearOpcion,
}: {
  ancla: React.RefObject<HTMLElement | null>
  prop: Propiedad
  elegidas: string[]
  multiple: boolean
  alElegir: (ids: string[]) => void
  alCerrar: () => void
  alCrearOpcion?: (propId: string, nombre: string) => string | null
}) {
  const [q, setQ] = useState('')
  const opciones = prop.options ?? []
  const filtradas = opciones.filter((o) => o.name.toLowerCase().includes(q.trim().toLowerCase()))
  const exacta = opciones.some((o) => o.name.toLowerCase() === q.trim().toLowerCase())

  const alternar = (id: string) => {
    if (multiple) {
      alElegir(elegidas.includes(id) ? elegidas.filter((x) => x !== id) : [...elegidas, id])
    } else {
      alElegir(elegidas[0] === id ? [] : [id])
    }
  }

  const crear = () => {
    const nombre = q.trim()
    if (!nombre || !alCrearOpcion) return
    const id = alCrearOpcion(prop.id, nombre)
    if (id) {
      setQ('')
      alElegir(multiple ? [...elegidas, id] : [id])
    }
  }

  return (
    <Flotante ancla={ancla} alCerrar={alCerrar} ancho={240}>
      <div className="selector">
        <input
          autoFocus
          className="selector__buscar"
          placeholder={alCrearOpcion ? 'Busca o crea una opción…' : 'Buscar…'}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            if (filtradas.length > 0 && (exacta || !alCrearOpcion)) alternar(filtradas[0].id)
            else crear()
          }}
          aria-label="Buscar opción"
        />
        <div className="selector__lista" role="listbox">
          {filtradas.map((o) => (
            <button key={o.id} role="option" aria-selected={elegidas.includes(o.id)}
                    className="selector__opcion" onClick={() => alternar(o.id)}>
              <Pastilla opcion={o} />
              {elegidas.includes(o.id) && <span className="selector__marca">✓</span>}
            </button>
          ))}
          {alCrearOpcion && q.trim() && !exacta && (
            <button className="selector__opcion selector__opcion--crear" onClick={crear}>
              Crear <strong>{q.trim()}</strong>
            </button>
          )}
          {filtradas.length === 0 && !q.trim() && (
            <div className="selector__vacio">Escribe para crear la primera opción</div>
          )}
        </div>
        {elegidas.length > 0 && (
          <button className="selector__quitar" onClick={() => alElegir([])}>Quitar valor</button>
        )}
      </div>
    </Flotante>
  )
}
